import { ActionCreatorsMapObject } from '@reduxjs/toolkit';
import { ThunkAction } from 'redux-thunk';

import UnauthenticatedUserSessionModel from 'gen-thrift/unauthenticated_user_session_Model_types';

import { IPublicContentViewEventService } from 'api/PublicContent/interfaces/IPublicContentViewEventService';

import { IUserAccountInfoReader } from 'api/UserAccount/interfaces/IUserAccountInfoReader';
import { UserAccountId } from 'api/UserAccount/model/UserAccountId';

import { UserAccountManagerImpl } from 'shared/lib/account/impl/UserAccountManagerImpl';
import { UserPasswordManagerImpl } from 'shared/lib/account/impl/UserPasswordManagerImpl';
import { UserSessionManagerImpl } from 'shared/lib/account/impl/UserSessionManagerImpl';
import { UnknownCookieException } from 'shared/lib/cookie/exceptions/UnknownCookieException';
import { ICookieReader } from 'shared/lib/cookie/interfaces/ICookieReader';
import { UnauthenticatedUserSessionManagerImpl } from 'shared/lib/unauthenticated_user_session/impl/UnauthenticatedUserSessionManagerImpl';

import { ISignUpFormState } from 'shared/components/SignUpOrSignIn/SignUpForm/reducers';
import { ISignUpAndLoginButtonsState } from '../SignUpAndLoginButtons/reducers';

import { Observer } from 'shared/utils/observer';

interface IPublicSiteStore {
    signUpAndLoginButtonsState : ISignUpAndLoginButtonsState;
}

export namespace UserSignUpFormActionInterfaces {
    export interface IUserSignUpFormActionCreatorsMapObject extends ActionCreatorsMapObject {
        onSuccessfulSignUp : (
            userAccountIdentifier : UserAccountId,
            signUpFormState : ISignUpFormState
        ) => ThunkAction<Promise<void>, IPublicSiteStore, {services : UserSignUpFormActionInterfaces.IServices}>;
    }

    export interface IServices {
        cookieReader : ICookieReader;
        publicContentViewEventService : IPublicContentViewEventService;
        unauthenticatedUserSessionManager : UnauthenticatedUserSessionManagerImpl;
        userSessionManager : UserSessionManagerImpl;
        userAccountInfoReader : IUserAccountInfoReader;
        userAccountManager : UserAccountManagerImpl;
        userPasswordManager : UserPasswordManagerImpl;
    }
}

const onSuccessfulSignUp = (
    userAccountId : UserAccountId,
    signUpFormState : ISignUpFormState
) : ThunkAction<Promise<void>, IPublicSiteStore, {services : UserSignUpFormActionInterfaces.IServices}> => {
    return (dispatch, getState, extraArguments) : Promise<void> => {
        const inputDataByFieldName = signUpFormState.validationInputDataByFieldName;

        let hubspotCookie : string | null = null;
        try {
            hubspotCookie = extraArguments.services.cookieReader.getCookie('hubspotutk');
        } catch (error) {
            if (!(error instanceof UnknownCookieException)) {
                throw error;
            }
        }

        return Promise.all([
            extraArguments.services.unauthenticatedUserSessionManager.getOrCreateUnauthenticatedUserSessionId(),
            Observer.trackConversion.userCreated({
                email: inputDataByFieldName.emailAddress.value,
                phone: inputDataByFieldName.phone.value,
                bar_name: inputDataByFieldName.barName.value,
                first_name: inputDataByFieldName.firstName.value,
                last_name: inputDataByFieldName.lastName.value,
                zip_code: inputDataByFieldName.optionalZipCode.value,
                subscribe_to_blog: inputDataByFieldName.subscribeToBlog.value,
                hubspot_cookie : hubspotCookie,
                is_admin_created: false,
                source_url: window.location.href,
                should_receive_marketing: true,
                creator_user_id: null,
                retailer_id: null,
            })
        ])
        .then((result) => {
            const unauthenticatedUserSessionId : UnauthenticatedUserSessionModel.UnauthenticatedUserSessionId = result[0];
            const userFullName = inputDataByFieldName.firstName.value + ' ' + inputDataByFieldName.lastName.value;
            Observer.identifyAuthenticated(userAccountId, userFullName, inputDataByFieldName.emailAddress.value, null);
            Observer.observeAction('New User Account Created', {
                referrer: document.referrer,
                page: window.location.pathname,
                unauthenticatedUserSessionId: unauthenticatedUserSessionId.value,
            });

            Observer.identifyUnauthenticated(unauthenticatedUserSessionId);
            Observer.observeAction('Clicked button to submit sign up form', {});
            Observer.observeAction('Successfully Submitted Sign Up Form', {
                userAccountId: userAccountId.getValue(),
            });
        });
    };
};

export const UserSignUpFormActions : UserSignUpFormActionInterfaces.IUserSignUpFormActionCreatorsMapObject = {
    onSuccessfulSignUp,
};
